import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import * as d3 from "d3";
import { ARAGON_COLORS, AragonFontLoader, useAragonViewport } from "../theme/AragonTheme";

export function useLayoutMappingMetrics() {
  const viewport = useAragonViewport();
  return useMemo(() => {
    const width = viewport.width;
    const height = viewport.height;
    return {
      viewport,
      sidebarCollapsed: Math.round(d3.scaleLinear().domain([768, 1920]).range([56, 64]).clamp(true)(width)),
      sidebarExpanded: Math.round(d3.scaleLinear().domain([768, 1067, 1920]).range([228, 298, 310]).clamp(true)(width)),
      sidebarItemHeight: Math.round(d3.scaleLinear().domain([768, 1920]).range([42, 48]).clamp(true)(width)),
      sidebarItemGap: Math.round(d3.scaleLinear().domain([768, 1920]).range([2, 5]).clamp(true)(width)),
      sidebarNavTop: Math.round(d3.scaleLinear().domain([620, 1080]).range([12, 18]).clamp(true)(height)),
      sidebarIconSize: Math.round(d3.scaleLinear().domain([768, 1920]).range([19, 22]).clamp(true)(width)),
      sidebarLabelSize: Math.round(d3.scaleLinear().domain([768, 1920]).range([12, 14]).clamp(true)(width)),
      logoSize: Math.round(d3.scaleLinear().domain([768, 1920]).range([22, 26]).clamp(true)(width)),
      mainCornerRadius: Math.round(d3.scaleLinear().domain([768, 1067, 1920]).range([12, 14, 16]).clamp(true)(width)),
      gutter: Math.round(d3.scaleLinear().domain([768, 1920]).range([22, 48]).clamp(true)(width)),
      cardGap: Math.round(d3.scaleLinear().domain([768, 1920]).range([12, 17]).clamp(true)(width)),
      cardPadding: Math.round(d3.scaleLinear().domain([768, 1920]).range([17, 23]).clamp(true)(width)),
      cardRadius: Math.round(d3.scaleLinear().domain([768, 1920]).range([9, 11]).clamp(true)(width)),
      titleSize: Math.round(d3.scaleLinear().domain([768, 1920]).range([23, 29]).clamp(true)(width)),
      sectionTitleSize: Math.round(d3.scaleLinear().domain([768, 1920]).range([16, 20]).clamp(true)(width)),
      bodySize: Math.round(d3.scaleLinear().domain([768, 1920]).range([11, 14]).clamp(true)(width)),
      eyebrowSize: Math.round(d3.scaleLinear().domain([768, 1920]).range([8, 10]).clamp(true)(width)),
      messageBubble: Math.round(d3.scaleLinear().domain([768, 1920]).range([44, 50]).clamp(true)(width)),
      messageInset: Math.round(d3.scaleLinear().domain([768, 1920]).range([18, 28]).clamp(true)(width)),
      messagePanelWidth: Math.round(d3.scaleLinear().domain([768, 1920]).range([255, 310]).clamp(true)(width)),
      compact: width < 760,
      tablet: width < 1040,
    };
  }, [viewport]);
}

export default function LayoutMapping({ metrics, sidebar, floatingUtility, children }) {
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const sidebarWidth = sidebarExpanded ? metrics.sidebarExpanded : metrics.sidebarCollapsed;
  const mappedSidebar = React.isValidElement(sidebar)
    ? React.cloneElement(sidebar, { onExpandedChange: setSidebarExpanded })
    : sidebar;
  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: ARAGON_COLORS.sellerSidebar, color: ARAGON_COLORS.navy }}>
      <AragonFontLoader />
      {mappedSidebar}
      <Box
        component="main"
        sx={{
          minHeight: "100dvh",
          ml: `${sidebarWidth}px`,
          bgcolor: ARAGON_COLORS.sellerCanvas,
          borderTopLeftRadius: `${metrics.mainCornerRadius}px`,
          overflow: "hidden",
          px: `${metrics.gutter}px`,
          pt: `${metrics.contentTop}px`,
          pb: `${Math.max(metrics.gutter, 34)}px`,
          boxSizing: "border-box",
          transition: "margin-left 220ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: `${metrics.contentWidth}px`, mx: "auto" }}>{children}</Box>
      </Box>
      {floatingUtility}
    </Box>
  );
}
